// @flow

import React from 'react'
import { useTranslation } from 'react-i18next'
import type { Node } from 'react'

import RequestTypeFields from './RequestTypeFields'
import Pan from './Tabs/Pan'
import RouterTabs from './Tabs/RouterTabs'
import Content from './Content'
import ActivityType from './ActivityType'
import ActivityPriority from './ActivityPriority'
import Salutation from './Salutation'
import ConfigItem from './ConfigItem'

import api from '../core/api'

const MAX_ROOM_TYPE_LENGTH = 500
const MAX_FLAT_CATEGORY_LENGTH = 100
const MAX_FLAT_CONTRACT_LENGTH = 30

const ObjectsAndFields = (): Node => {
  const { t } = useTranslation('ObjectsAndFields')

  return (
    <Content style={{ marginTop: '20px' }}>
      <section className='unit unit--default'>
        <RouterTabs>
          <Pan
            id='objects_and_fields_requests_tab'
            label={t('Requests')}
            linkHash='#requests-fields'
            count={1}
          >
            <RequestTypeFields />
          </Pan>
          <Pan
            id='objects_and_fields_activity_type_tab'
            label={t('ActivityType')}
            linkHash='#activity-types'
          >
            <ActivityType />
          </Pan>
          <Pan
            id='objects_and_fields_activity_priority_tab'
            label={t('ActivityPriority')}
            linkHash='#activity-priorities'
          >
            <ActivityPriority />
          </Pan>

          <Pan id='BudgetPlan' label={t('BudgetPlan')} linkHash='#budget-plan'>
            <ConfigItem
              actionTitle={t('BudgetPlan:EditInvoiceType')}
              addListItemPlaceholder={t('BudgetPlan:NewInvoiceTypePlaceholder')}
              addListItemTitle={t('BudgetPlan:AddNewInvoiceType')}
              createApi={api.budgetPlan.createInvoiceType}
              listApi={api.budgetPlan.getInvoiceTypes}
              listTitle={t('BudgetPlan:InvoiceTypeList')}
              modalTitle={t('BudgetPlan:ChangeInvoiceTypeTitle')}
              title={t('BudgetPlan:InvoiceType')}
            />
            <ConfigItem
              actionTitle={t('BudgetPlan:EditInvoicePaymentType')}
              addListItemPlaceholder={t('BudgetPlan:NewInvoiceTypePlaceholder')}
              addListItemTitle={t('BudgetPlan:AddNewInvoicePaymentType')}
              createApi={api.budgetPlan.createPaymentType}
              listApi={api.budgetPlan.getPaymentTypes}
              listTitle={t('BudgetPlan:InvoicePaymentTypeList')}
              modalTitle={t('BudgetPlan:ChangeInvoicePaymentTypeTitle')}
              title={t('BudgetPlan:InvoicePaymentType')}
            />
          </Pan>
          <Pan
            id='objects_and_fields_salutation_tab'
            label={t('Salutation')}
            linkHash='#salutation'
          >
            <Salutation />
          </Pan>
          <Pan id='technical' label={t('Technical')} linkHash='#technical'>
            <ConfigItem
              actionTitle={t('Flat:EditRoomType')}
              addListItemPlaceholder={t('Flat:AddRoomTypePlaceholder')}
              addListItemTitle={t('Flat:AddRoomType')}
              createApi={api.roomType.createRoomType}
              listApi={api.roomType.getRoomType}
              listTitle={t('Flat:RoomTypeList')}
              maxValueLength={MAX_ROOM_TYPE_LENGTH}
              modalTitle={t('Flat:AddRoomTypeTitle')}
              title={t('Flat:EditRoomType')}
            />
            <ConfigItem
              actionTitle={t('Building:EditLivingType')}
              addListItemPlaceholder={t('Building:AddLivingTypePlaceholder')}
              addListItemTitle={t('Building:AddLivingType')}
              createApi={api.livingType.createLivingType}
              listApi={api.livingType.getLivingType}
              listTitle={t('Building:LivingTypeList')}
              modalTitle={t('Building:LivingTypeTitle')}
              title={t('Building:EditLivingType')}
            />
            <ConfigItem
              actionTitle={t('Building:EditInsuranceType')}
              addListItemPlaceholder={t('Building:AddLivingTypePlaceholder')}
              addListItemTitle={t('Building:AddInsuranceType')}
              createApi={api.insurance.createInsuranceType}
              listApi={api.insurance.getInsuranceType}
              listTitle={t('Building:InsuranceTypeList')}
              modalTitle={t('Building:InsuranceTypeTitle')}
              title={t('Building:EditInsuranceType')}
            />
            <ConfigItem
              actionTitle={t('Profile:EditStatus')}
              addListItemPlaceholder={t('Profile:AddStatusPlaceholder')}
              addListItemTitle={t('Profile:AddStatus')}
              createApi={api.status.createInfoStatus}
              listApi={api.status.getInfoStatus}
              listTitle={t('Profile:StatusList')}
              modalTitle={t('Profile:Status')}
              title={t('Profile:Status')}
            />
            <ConfigItem
              actionTitle={t('Profile:EditPreferredCommunication')}
              addListItemPlaceholder={t(
                'Profile:AddPreferredCommunicationPlaceholder'
              )}
              addListItemTitle={t('Profile:AddPreferredCommunication')}
              createApi={
                api.prefferedCommunication.createPreferredCommunication
              }
              listApi={api.prefferedCommunication.getPreferredCommunication}
              listTitle={t('Profile:PreferredCommunicationsList')}
              modalTitle={t('Profile:PreferredCommunication')}
              title={t('Profile:PreferredCommunication')}
            />
            <ConfigItem
              actionTitle={t('Flat:EditFlatCondition')}
              addListItemPlaceholder={t('Flat:AddFlatConditionPlaceholder')}
              addListItemTitle={t('Flat:AddFlatCondition')}
              createApi={api.flatCondition.createFlatCondition}
              listApi={api.flatCondition.getFlatCondition}
              listTitle={t('Flat:FlatConditionList')}
              maxValueLength={MAX_FLAT_CATEGORY_LENGTH}
              modalTitle={t('Flat:FlatConditionTitle')}
              title={t('Flat:FlatCondition')}
            />
            <ConfigItem
              actionTitle={t('Flat:EditFlatCategory')}
              addListItemPlaceholder={t('Flat:AddFlatCategoryPlaceholder')}
              addListItemTitle={t('Flat:AddFlatCategory')}
              createApi={api.flatCategory.createFlatCategory}
              listApi={api.flatCategory.getFlatCategory}
              listTitle={t('Flat:FlatCategoryList')}
              maxValueLength={MAX_FLAT_CATEGORY_LENGTH}
              modalTitle={t('Flat:FlatCategoryTitle')}
              title={t('Flat:FlatCategory')}
            />
            <ConfigItem
              actionTitle={t('Flat:EditFlatContract')}
              addListItemPlaceholder={t('Flat:AddFlatContractPlaceholder')}
              addListItemTitle={t('Flat:AddFlatContract')}
              createApi={api.flatContract.createFlatContract}
              listApi={api.flatContract.getFlatContract}
              listTitle={t('Flat:FlatContractList')}
              maxValueLength={MAX_FLAT_CONTRACT_LENGTH}
              modalTitle={t('Flat:FlatContractTitle')}
              title={t('Flat:FlatContract')}
            />
            <ConfigItem
              actionTitle={t('Flat:EditFlatSpecifics')}
              addListItemPlaceholder={t('Flat:AddFlatSpecificsPlaceholder')}
              addListItemTitle={t('Flat:AddFlatSpecifics')}
              createApi={api.flatSpecifics.createFlatSpecifics}
              listApi={api.flatSpecifics.getFlatSpecifics}
              listTitle={t('Flat:FlatSpecificsList')}
              maxValueLength={MAX_FLAT_CATEGORY_LENGTH}
              modalTitle={t('Flat:FlatSpecificsTitle')}
              title={t('Flat:FlatSpecifics')}
            />
          </Pan>
        </RouterTabs>
      </section>
    </Content>
  )
}

export default ObjectsAndFields
